// vendor modules
import React from "react"
import PropTypes from "prop-types"

// internal modules
import EnumFilterItem from "app/static/frontend/search-new/components/Filter/EnumFilter/EnumFilterItem"
import EnumGroupHeader from "app/static/frontend/search-new/components/Filter/EnumFilter/EnumGroupHeader"

// components
import { SelectSearchBar as StyledSelectWrapper } from "quorumdesign"

/** ***************************************************************************
 *
 * EnumFilter is the collective grouping of all of the EnumFilterItems. It gets
 * passed the updateFilter prop from Filter.js, as well as the de-serialized value
 * of the filter from the store. In this case, the value of the filter should be
 * an array of integers.
 *
 ***************************************************************************** */

export class EnumFilter extends React.Component {
    static propTypes = {
        uniqueFlvId: PropTypes.string.isRequired,
        uniqueFilterId: PropTypes.string.isRequired,
        publishChange: PropTypes.func.isRequired,
        deserializedValue: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
            PropTypes.string,
            PropTypes.number,
        ]),
        shouldRenderSelectAll: PropTypes.bool,
        groupHelpText: PropTypes.string,
        dictDisplayKey: PropTypes.string,
        multi: PropTypes.bool,
        isNullable: PropTypes.bool,
        enumItems: PropTypes.array,
        enumGroups: PropTypes.array,
        enumResource: PropTypes.object,
        displayLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        helpText: PropTypes.string,
        displayKey: PropTypes.string,
        sortBy: PropTypes.func,
        selectComponent: PropTypes.func,
        // selected from the search => props.uniqueFlvId slice of the store
        searchFilterTerm: PropTypes.string,
        // from app/static/frontend/search-new/components/Filter/index.js
        setFilterContainsSearchFilterTerm: PropTypes.func,
        // from app/static/frontend/search-new/components/FilterGroup/index.js renderFilters
        setFilterGroupContainsSearchFilterTerm: PropTypes.func,
    }

    static defaultProps = {
        multi: true,
        useTip: true,
        shouldRenderSelectAll: true,
        isNullable: true,
    }

    // Logic about opening / closing filters.
    isItemActive = (itemId) =>
        Array.isArray(this.props.deserializedValue)
            ? this.props.deserializedValue.includes(itemId)
            : this.props.deserializedValue === itemId

    // If an item clicked, check if the value is currently active. If so,
    // check if this filter isNullable, if not, do nothing, otherwise,
    // deselect it. If it's not active, activate it.
    // Modify the list of active filters accordingly.
    updateEnumFilter = (id) => {
        if (!this.props.multi) {
            const newVal = this.isItemActive(id) ? [] : [id]

            if (this.props.isNullable) {
                return this.props.publishChange(newVal)
            }

            if (newVal.length) {
                return this.props.publishChange(newVal)
            }
        } else {
            const deselectItem = (idNew) => this.props.deserializedValue.filter((val) => val !== idNew)
            const selectItem = (idNew) => [...this.props.deserializedValue, idNew]
            const newValue = this.isItemActive(id) ? deselectItem(id) : selectItem(id)

            return this.props.publishChange(newValue)
        }
    }

    updateSelect = (data) => {
        if (!data) {
            this.props.publishChange(undefined)
            return
        }
        // the data will come back in an array if it's multi, otherwise will be a single object
        // that will need to be tossed into an array in order to make serialization work
        const formattedData = this.props.multi ? data.map((obj) => obj.id) : [data.id]

        this.props.publishChange(formattedData)
    }

    // Render logic!
    renderEnumInline = (item) => {
        // .toString necessary because some labels are integers (2021, 2020, etc.)
        const label = (item[this.props.displayKey] || item.label).toString()
        if (
            this.props.searchFilterTerm &&
            label &&
            label.toLowerCase().includes(this.props.searchFilterTerm.toLowerCase())
        ) {
            if (this.props.setFilterGroupContainsSearchFilterTerm) {
                this.props.setFilterGroupContainsSearchFilterTerm(true)
            } else {
                // not marked required
                console.error(
                    `Missing props.setFilterGroupContainsSearchFilterTerm — ${this.props.searchFilterTerm} contained in "${label}".`,
                )
            }
            if (this.props.setFilterContainsSearchFilterTerm) {
                this.props.setFilterContainsSearchFilterTerm(true)
            } else {
                // not marked required
                console.error(
                    `Missing props.setFilterContainsSearchFilterTerm — ${this.props.searchFilterTerm} contained in "${label}".`,
                )
            }
        }

        return (
            <EnumFilterItem
                key={item.value}
                id={item.value}
                onPressItem={this.updateEnumFilter}
                label={label}
                helpText={this.props.useTip && item.tip}
                isActive={this.isItemActive(item.value)}
                multi={this.props.multi}
                searchFilterTerm={this.props.searchFilterTerm}
            />
        )
    }

    renderGroup = (group) => {
        if (
            this.props.searchFilterTerm &&
            group.title &&
            group.title.toString().toLowerCase().includes(this.props.searchFilterTerm.toLowerCase())
        ) {
            if (this.props.setFilterGroupContainsSearchFilterTerm) {
                this.props.setFilterGroupContainsSearchFilterTerm(true)
            } else {
                // not marked required
                console.error(
                    `Missing props.setFilterGroupContainsSearchFilterTerm — ${this.props.searchFilterTerm} contained in "${group.title}".`,
                )
            }

            if (this.props.setFilterContainsSearchFilterTerm) {
                this.props.setFilterContainsSearchFilterTerm(true)
            } else {
                // not marked required
                console.error(
                    `Missing props.setFilterContainsSearchFilterTerm — ${this.props.searchFilterTerm} contained in "${group.title}".`,
                )
            }
        }

        if (!group.title) {
            return null
        }

        return (
            <div className="enum-group" key={group.title}>
                <EnumGroupHeader
                    title={group.title}
                    publishChange={this.props.publishChange}
                    enumValues={group.data.map((item) => item.value)}
                    deserializedValue={this.props.deserializedValue}
                    shouldRenderSelectAll={this.props.shouldRenderSelectAll}
                    groupHelpText={group.helpText}
                    searchFilterTerm={this.props.searchFilterTerm}
                    isCollapsedGroup={this.props.uniqueFilterId === "stafferTypeFilter"}
                />
                {/* If there is just one subItem in a group, and we want to collapse it, do so */}
                {!(group.data.length <= 6 && group.collapseGroup) && (
                    <div className="enum-inner-group-design-system">
                        {group.sub_group &&
                        group.sub_group.filter((g) => g.regions === Userdata.getCurrentRegionText()).length
                            ? group.sub_group.map((_group) => this.renderGroup(_group))
                            : group.data.map((item) => this.renderEnumInline(item))}
                    </div>
                )}
            </div>
        )
    }

    renderEnums = (sortedItems) => {
        // take the list of enumGroups, which are formatted { values: [id1, id2, id3, ...], title: 'Title', help_text: 'Help Text' }
        // and convert them into a format readable by SectionList { data: [obj1, obj2, obj3], title: 'Title', help_text: 'Help Text' }
        if (this.props.enumGroups) {
            // This is responsible for traversing through all the sub_groups and adding the 'data' prop.
            // Return: parent group object with all the formatted nested subgroups.
            const getFormattedParentGroup = (filterGroup) => {
                const hasBeenVisited = (visitedArr, current) => visitedArr.find((elem) => elem.title === current.title)
                const visited = []
                const q = [filterGroup]
                while (q.length) {
                    const cur = q.pop()
                    if (!hasBeenVisited(visited, cur)) {
                        // format by adding 'data' prop here to get enum relevant
                        cur.data = sortedItems.filter(
                            (item) => cur.values.includes(item.value) && cur.type === item.type,
                        )
                        visited.push(cur)
                        if (cur.sub_group && cur.sub_group.length) {
                            for (let i = 0; i < cur.sub_group.length; i++) {
                                q.push(cur.sub_group[i])
                            }
                        }
                    }
                }
                return visited[0] // grabbing just the 'parent' group
            }

            const sections = this.props.enumGroups
                .map((group) => {
                    const resortedItems = group.sortBy ? sortedItems.slice().sort(group.sortBy) : sortedItems

                    return group.sub_group
                        ? getFormattedParentGroup(group)
                        : {
                              data: resortedItems.filter(
                                  (item) => group.values.includes(item.value) && group.type === item.type,
                              ),
                              title: group.title,
                              helpText: group.help_text,
                              collapseGroup: group.collapseGroup,
                          }
                })
                .filter((group) => group.data.length)

            return sections.map((group) => this.renderGroup(group))
        }

        return sortedItems.map((item) => this.renderEnumInline(item))
    }

    renderSelect = (sortedItems) => {
        const SelectComponent = this.props.selectComponent

        return (
            <StyledSelectWrapper>
                <SelectComponent
                    value={this.props.deserializedValue}
                    onChange={this.updateSelect}
                    multi={this.props.multi}
                    customOptions={sortedItems}
                    placeholder={this.props.placeholder}
                />
            </StyledSelectWrapper>
        )
    }

    render() {
        const sortedItems = this.props.sortBy ? this.props.enumItems.sort(this.props.sortBy) : this.props.enumItems

        return this.props.selectComponent && this.props.enumItems.length >= 4 ? (
            this.renderSelect(sortedItems)
        ) : (
            <div>{sortedItems && this.renderEnums(sortedItems)}</div>
        )
    }
}

export default EnumFilter
